import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useApi, type CleaningTaskQueueResponse } from '@/shared/api';
import { cleaningFailure } from '../cleaningFailure';
import type { PlanStep } from './myTasksModel';

type Reached = 'nothing' | 'claimed' | 'started';

/** Wie weit die Kette kam, bevor ein Schritt scheiterte — der Satz sagt, was schon geschehen ist. */
class StepError extends Error {
  constructor(
    readonly original: unknown,
    readonly reached: Reached,
  ) {
    super('cleaning step failed');
  }
}

export interface StepVars {
  task: CleaningTaskQueueResponse;
  step: PlanStep;
  notes?: string;
  /** Übernehmen statt nur starten; Standard `canClaim`. Wer zuteilt, startet ohne Übernahme. */
  claim?: boolean;
}

export async function stepFailure(error: unknown, step: PlanStep): Promise<string> {
  const reached = error instanceof StepError ? error.reached : 'nothing';
  const failure = await cleaningFailure(
    error instanceof StepError ? error.original : error,
    'Das hat nicht geklappt.',
  );
  if (failure.unknown) return failure.text;
  if (reached === 'claimed')
    return `Übernommen und gestartet, aber nicht erledigt: ${failure.text}`;
  if (reached === 'started') return `Gestartet, aber nicht erledigt: ${failure.text}`;
  return step === 'finish' ? `Nicht erledigt gemeldet: ${failure.text}` : failure.text;
}

/**
 * Die Schritte am Tagesplan und in der Aufgabe. „Erledigt melden“ ohne Fotos verkettet die
 * vorhandenen Endpunkte (übernehmen und starten oder starten, dann abschließen) — dieselben Schritte
 * wie bisher, nur ohne Warten dazwischen. Den Endstand (erledigt oder mit Meldung) setzt der Server.
 */
export function useTaskStep({
  onFailure,
}: { onFailure?: (message: string, vars: StepVars) => void } = {}) {
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({ task, step, notes, claim = task.canClaim }: StepVars) => {
      if (step === 'continue') return null;
      let reached: Reached = 'nothing';
      try {
        if (claim) {
          await api.cleaning.claimAndStart(task.id);
          reached = 'claimed';
        } else if (task.status === 'Pending') {
          await api.cleaning.start(task.id);
          reached = 'started';
        }
        if (step !== 'finish') return null;
        return await api.cleaning.complete(task.id, { completionNotes: notes?.trim() || null });
      } catch (error) {
        throw new StepError(error, reached);
      }
    },
    // Ohne Netz sperrt die Oberfläche den Knopf; pausieren ließe ihn endlos drehen.
    networkMode: 'always',
    onSuccess: (result, { task, step }) => {
      if (step === 'finish')
        toast.success(
          result?.status === 'Issue'
            ? `${task.propertyName} erledigt – mit offener Meldung`
            : `${task.propertyName} erledigt`,
        );
      else void navigate({ to: '/cleaning/$id', params: { id: task.id } });
    },
    onError: async (error, vars) => {
      const message = await stepFailure(error, vars.step);
      if (onFailure) onFailure(message, vars);
      else toast.error(message);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['cleaning'] }),
  });
}
